/* eslint-disable max-len */
import { useFormikContext } from 'formik';
import { ReactElement, useEffect, useRef, useState } from 'react';
import ActionButton from 'src/components/ActionButton/ActionButton';
import FileUploadStatus from 'src/components/FileUploadStatus/FileUploadStatus';
import FormInputLabel from 'src/components/FormInputs/FormInputLabel/FormInputLabel';
import Panel from 'src/components/Panel/Panel';
import { useConfirmationModalContext } from 'src/hooks/useConfirmationModalContext';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  selectExitAdjustmentDataEntryMode,
  selectExitAdjustmentError,
  selectExitAdjustmentStudentFiles,
  setStudentFiles,
} from 'src/redux/slices/exitAdjustmentSlice';
import {
  deleteExitAdjustmentFileThunk,
  fetchExitAdjustmentFilesByRequestThunk,
  uploadExitAdjustmentFileThunk,
} from 'src/redux/thunks/exitAdjustmentThunks';
import {
  getMaxFileUploadSizeMB,
  getMaxUploadFilenameLength,
} from 'src/services/fileUpload.service';
import { DataEntryMode } from 'src/types/DataEntryMode';
import { ExitAdjustmentFileListItemModel } from 'src/types/ExitAdjustmentFileListItemModel';
import { ExitAdjustmentModel } from 'src/types/ExitAdjustmentModel';
import { PanelType } from 'src/types/PanelType';
import { createDownloadLinkAndExecute } from 'src/utilities/formUtilities';
import { isDistrictUser, isStateAdmin } from 'src/utilities/userUtilities';
import './ExitAdjustmentUploadDocuments.css';

interface UploadedDocument {
  id: string;
  documentName: string;
  uploadedBy: string;
  uploadDate: string;
}

interface FileUploadState {
  progress: number;
  isCompleted: boolean;
  isFailed: boolean;
  error?: string;
}

interface ExitAdjustmentUploadDocumentsProps {
  associatedRecordId?: string;
  schoolYear?: string;
  studentIdentifier?: string;
  leaIdentifier?: string;
  studentFiles?: ExitAdjustmentFileListItemModel[];
  disabled?: boolean;
  studentUpdate?: boolean;
  reviewStatus?: string;
}

const EMPTY_STUDENT_FILES: ExitAdjustmentFileListItemModel[] = [];

const ExitAdjustmentUploadDocuments = ({
  associatedRecordId,
  schoolYear,
  studentIdentifier,
  leaIdentifier,
  studentFiles,
  disabled = false,
  studentUpdate = false,
  reviewStatus,
}: ExitAdjustmentUploadDocumentsProps): ReactElement => {
  const dispatch = useAppDispatch();
  const { values, touched, dirty, setFieldValue, setFieldTouched } =
    useFormikContext<ExitAdjustmentModel>();
  const dataEntryMode = useAppSelector(selectExitAdjustmentDataEntryMode);
  const reduxError = useAppSelector(selectExitAdjustmentError);
  const contextStudentFiles = useAppSelector(selectExitAdjustmentStudentFiles);
  const { showConfirmation, setOptions } = useConfirmationModalContext();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [newlyUploadedFiles, setNewlyUploadedFiles] = useState<
    ExitAdjustmentFileListItemModel[]
  >([]);

  const isReadOnlyUser = (): boolean => {
    if (isStateAdmin() || isDistrictUser()) return false;
    return true;
  };

  const filesToUse =
    contextStudentFiles.length > 0
      ? contextStudentFiles
      : studentFiles || EMPTY_STUDENT_FILES;

  function getLastSegmentFromFilename(filename: string): string {
    const parts = filename.split(';');
    return parts[parts.length - 1];
  }

  const uploadedDocuments: UploadedDocument[] = filesToUse.map((file) => ({
    id: file.id,
    documentName: getLastSegmentFromFilename(file.fileName),
    uploadedBy: file.uploadedBy || 'Unknown',
    uploadDate: file.uploadedDate ? file.uploadedDate.split(' ')[0] : 'Unknown',
  }));

  const [uploadState, setUploadState] = useState<FileUploadState>({
    progress: 0,
    isCompleted: false,
    isFailed: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isUploadDisabled = disabled || !dirty || !associatedRecordId;
  const normalizedReviewStatus = reviewStatus?.toUpperCase() ?? '';
  const isApproved = normalizedReviewStatus === 'APPROVED';
  // District users may edit exit type/date and upload additional docs while
  // pending. Existing submitted docs cannot be removed, but files uploaded
  // in this session (not yet re-submitted) can still be deleted.
  const isDocumentDeleteDisabled = (documentId: string): boolean => {
    const isNewlyUploaded = newlyUploadedFiles.some(
      (file) => file.id === documentId
    );
    return (
      disabled ||
      isReadOnlyUser() ||
      !studentUpdate ||
      isApproved ||
      (isDistrictUser() &&
        normalizedReviewStatus === 'PENDING APPROVAL' &&
        !isNewlyUploaded)
    );
  };

  useEffect(() => {
    const hasRequiredSupportingDocuments = filesToUse.length > 0;
    const hasFileValidationError = !!fileError;
    const hasUploadedDocuments =
      hasRequiredSupportingDocuments && !hasFileValidationError;

    // In edit mode, avoid mutating form state while pristine.
    // Existing files may load asynchronously and otherwise mark form dirty.
    if (
      dataEntryMode === DataEntryMode.EDIT &&
      !dirty &&
      values.documentsUploadedToSharePoint !== hasUploadedDocuments
    ) {
      return;
    }

    if (values.documentsUploadedToSharePoint !== hasUploadedDocuments) {
      setFieldValue(
        'documentsUploadedToSharePoint',
        hasUploadedDocuments,
        true
      );
    }

    if (!touched.documentsUploadedToSharePoint) {
      setFieldTouched('documentsUploadedToSharePoint', true, false);
    }
  }, [
    filesToUse,
    newlyUploadedFiles,
    fileError,
    dirty,
    dataEntryMode,
    values.documentsUploadedToSharePoint,
    touched.documentsUploadedToSharePoint,
    setFieldValue,
    setFieldTouched,
  ]);

  useEffect(() => {
    if (associatedRecordId && leaIdentifier) {
      dispatch(
        fetchExitAdjustmentFilesByRequestThunk({
          leaId: leaIdentifier,
          associatedRecordId,
          showLoading: false,
        })
      );
    }
  }, [associatedRecordId, leaIdentifier, dispatch]);

  const validateFile = (file: File): string => {
    const allowedExtensions = ['.pdf'];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(fileExtension)) {
      const error = 'Invalid file type, must be .pdf';
      console.error({ error, hasError: true });
      return error;
    }

    const maxSizeMB: number = getMaxFileUploadSizeMB();
    const maxSize = maxSizeMB * 1024 * 1024;

    if (file.size > maxSize) {
      const error = 'Files cannot be larger than 50 MB';
      console.error({ error, hasError: true });
      return error;
    }

    const maxFilenameLength = getMaxUploadFilenameLength();
    if (file.name.length > maxFilenameLength) {
      const error = 'Filename exceeds max length of 50 characters';
      console.error({ error, hasError: true });
      return error;
    }

    return '';
  };

  useEffect((): (() => void) => {
    const fileInput = fileInputRef.current;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (!fileInput) return (): void => { };

    const handleInputChange = (event: Event): void => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        const error = validateFile(file);
        if (error) {
          setFileError(error);
          setSelectedFile(file);
        } else {
          setFileError('');
          setSelectedFile(file);
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleInputError = (event: Event): void => {
      setFileError(
        `Error selecting file. The file might be too large or corrupted.`
      );
      setSelectedFile(null);
    };

    fileInput.addEventListener('change', handleInputChange);
    fileInput.addEventListener('error', handleInputError);

    return () => {
      fileInput.removeEventListener('change', handleInputChange);
      fileInput.removeEventListener('error', handleInputError);
    };
  }, []);

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];

    if (file) {
      if (file.size === 0) {
        const error =
          'File appears to be empty or corrupted. Please try a different file.';
        setFileError(error);
        setSelectedFile(file);
        console.error({ error, hasError: true });
        return;
      }

      const error = validateFile(file);

      if (error) {
        setFileError(error);
        setSelectedFile(file);
      } else {
        setFileError('');
        setSelectedFile(file);
      }
    } else {
      const error =
        'File is too large for the browser to handle. Please try a smaller file.';
      setFileError(error);
      setSelectedFile(null);
      console.error({ error, hasError: true });
    }
  };

  const handleChooseFiles = (): void => {
    if (fileInputRef.current) {
      setFileError('');
      fileInputRef.current.click();

      setTimeout(() => {
        if (fileInputRef.current && fileInputRef.current.files?.length === 0) {
          console.error(
            'No file selected after timeout - indicates browser limitation'
          );
        }
      }, 1000);
    }
  };

  const handleClearFile = (): void => {
    setSelectedFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTryAgain = (): void => {
    setSelectedFile(null);
    setFileError('');
    setUploadState({
      progress: 0,
      isCompleted: false,
      isFailed: false,
    });
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadFile = async (): Promise<void> => {
    if (!selectedFile) return;

    const error = validateFile(selectedFile);
    if (error) {
      console.error({ error, hasError: true });
      setFileError(error);
      return;
    }

    setIsUploading(true);
    setUploadState({
      progress: 0,
      isCompleted: false,
      isFailed: false,
    });

    const progressInterval = setInterval(() => {
      setUploadState((prevState) => {
        if (prevState.progress < 0.9) {
          return {
            ...prevState,
            progress: Math.min(prevState.progress + 0.1, 0.9),
          };
        }
        return prevState;
      });
    }, 200);

    try {
      const currentSchoolYear = schoolYear || '2024-2025';
      const currentStudentIdentifier = studentIdentifier || '';

      if (!currentStudentIdentifier) {
        throw new Error('Student identifier is required for file upload');
      }

      const result = await dispatch(
        uploadExitAdjustmentFileThunk({
          schoolYear: currentSchoolYear,
          studentIdentifier: currentStudentIdentifier,
          leaIdentifier: leaIdentifier || '',
          associatedRecordId: associatedRecordId || '',
          file: selectedFile,
        })
      );

      clearInterval(progressInterval);

      if (uploadExitAdjustmentFileThunk.fulfilled.match(result)) {
        const uploadedFileId = result.payload?.id;

        setUploadState({
          progress: 1,
          isCompleted: true,
          isFailed: false,
        });

        if (associatedRecordId && leaIdentifier) {
          try {
            const fetchFiles = async (): Promise<
              ExitAdjustmentFileListItemModel[] | null
            > => {
              const fetchResult = await dispatch(
                fetchExitAdjustmentFilesByRequestThunk({
                  leaId: leaIdentifier,
                  associatedRecordId,
                  showLoading: false,
                })
              );

              if (
                fetchExitAdjustmentFilesByRequestThunk.fulfilled.match(
                  fetchResult
                )
              ) {
                return fetchResult.payload;
              }
              return null;
            };

            let latestFiles = await fetchFiles();

            // Upload endpoint returns only { id }. If metadata propagation is delayed,
            // retry once so the newly uploaded file appears in the table/context.
            if (
              uploadedFileId &&
              latestFiles &&
              !latestFiles.some((file) => file.id === uploadedFileId)
            ) {
              await new Promise((resolve) => setTimeout(resolve, 300));
              latestFiles = await fetchFiles();
            }

            if (latestFiles) {
              dispatch(setStudentFiles(latestFiles));

              const newFiles = latestFiles.filter(
                (file: ExitAdjustmentFileListItemModel) =>
                  !contextStudentFiles.some(
                    (existingFile: ExitAdjustmentFileListItemModel) =>
                      existingFile.id === file.id
                  )
              );
              setNewlyUploadedFiles([...newlyUploadedFiles, ...newFiles]);
            }
          } catch (fetchError) {
            console.error(
              'Error fetching updated files after upload:',
              fetchError
            );
          }
        }

        setTimeout(() => {
          handleClearFile();
          setUploadState({
            progress: 0,
            isCompleted: false,
            isFailed: false,
          });
        }, 1500);
      } else if (uploadExitAdjustmentFileThunk.rejected.match(result)) {
        let errorMessage =
          result.error?.message ||
          reduxError ||
          'Failed to upload file. Please try again.';

        if (
          errorMessage.includes('500') ||
          errorMessage.includes('Internal Server Error')
        ) {
          errorMessage =
            'The server encountered an error while processing your file. Please try again.';
        } else if (
          errorMessage.includes('400') ||
          errorMessage.includes('Bad Request')
        ) {
          errorMessage =
            'The file could not be processed. Please check the file format and try again.';
        } else if (
          errorMessage.includes('413') ||
          errorMessage.includes('Request Entity Too Large')
        ) {
          errorMessage = 'The file is too large. Please choose a smaller file.';
        } else if (
          errorMessage.includes('timeout') ||
          errorMessage.includes('Timeout')
        ) {
          errorMessage = 'The upload timed out. Please try again.';
        }

        setUploadState({
          progress: 0,
          isCompleted: false,
          isFailed: true,
          error: errorMessage,
        });
        return;
      } else {
        throw new Error('Upload failed');
      }
    } catch (uploadError) {
      clearInterval(progressInterval);

      console.error('Upload error:', uploadError);
      let errorMessage =
        uploadError instanceof Error &&
          uploadError.message === 'Student identifier is required for file upload'
          ? 'Please enter a student MSIS ID before uploading documents.'
          : reduxError || 'Failed to upload file. Please try again.';

      if (
        errorMessage.includes('500') ||
        errorMessage.includes('Internal Server Error')
      ) {
        errorMessage =
          'The server encountered an error while processing your file. Please try again.';
      } else if (
        errorMessage.includes('400') ||
        errorMessage.includes('Bad Request')
      ) {
        errorMessage =
          'The file could not be processed. Please check the file format and try again.';
      } else if (
        errorMessage.includes('413') ||
        errorMessage.includes('Request Entity Too Large')
      ) {
        errorMessage = 'The file is too large. Please choose a smaller file.';
      } else if (
        errorMessage.includes('timeout') ||
        errorMessage.includes('Timeout')
      ) {
        errorMessage = 'The upload timed out. Please try again.';
      }

      setUploadState({
        progress: 0,
        isCompleted: false,
        isFailed: true,
        error: errorMessage,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDocument = async (
    fileDocument: ExitAdjustmentFileListItemModel
  ): Promise<void> => {
    if (!leaIdentifier) {
      console.error('LEA identifier is required to download files.');
      return;
    }

    try {
      const { downloadExitAdjustmentFile } = await import(
        'src/services/exitAdjustment.service'
      );
      const blob = await downloadExitAdjustmentFile(
        leaIdentifier,
        fileDocument.id
      );

      createDownloadLinkAndExecute(fileDocument.fileName, blob);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleDeleteDocument = async (documentId: string): Promise<void> => {
    if (!leaIdentifier) {
      setDeleteError('LEA identifier is required to delete files.');
      return;
    }

    const documentToDelete = filesToUse.find(
      (file: ExitAdjustmentFileListItemModel) => file.id === documentId
    );
    const documentName = documentToDelete?.fileName || 'this document';

    setOptions({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete "${getLastSegmentFromFilename(
        documentName
      )}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setIsDeleting(documentId);
          setDeleteError('');

          const result = await dispatch(
            deleteExitAdjustmentFileThunk({
              leaId: leaIdentifier,
              id: documentId,
            })
          );

          if (deleteExitAdjustmentFileThunk.fulfilled.match(result)) {
            const updatedFiles = filesToUse.filter(
              (file: ExitAdjustmentFileListItemModel) => file.id !== documentId
            );
            dispatch(setStudentFiles(updatedFiles));

            setNewlyUploadedFiles(
              newlyUploadedFiles.filter(
                (file: ExitAdjustmentFileListItemModel) =>
                  file.id !== documentId
              )
            );

            if (associatedRecordId && leaIdentifier) {
              try {
                await new Promise((resolve) => setTimeout(resolve, 100));

                const fetchResult = await dispatch(
                  fetchExitAdjustmentFilesByRequestThunk({
                    leaId: leaIdentifier,
                    associatedRecordId,
                    showLoading: false,
                  })
                );

                if (
                  fetchExitAdjustmentFilesByRequestThunk.fulfilled.match(
                    fetchResult
                  )
                ) {
                  dispatch(setStudentFiles(fetchResult.payload || []));

                  setNewlyUploadedFiles(
                    newlyUploadedFiles.filter(
                      (file: ExitAdjustmentFileListItemModel) =>
                        file.id !== documentId
                    )
                  );
                } else {
                  console.warn(
                    'Refetch after delete failed, keeping optimistic update'
                  );
                }
              } catch (fetchError) {
                console.error(
                  'Error fetching updated files after delete:',
                  fetchError
                );
              }
            }
          } else {
            throw new Error('Delete failed');
          }
        } catch (error) {
          console.error('Delete error:', error);
          setDeleteError('Failed to delete file. Please try again.');
        } finally {
          setIsDeleting(null);
        }
        return {
          status: 200,
          data: null,
          error: { type: '', title: '', status: 200, detail: '' },
          aborted: false,
        };
      },
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onCancel: () => { },
    });

    const confirmed = await showConfirmation();
    if (!confirmed) {
      return;
    }
  };

  return (
    <div className="exit-adjustment-upload-documents">
      <Panel panelType={PanelType.INFO} heading="Upload Documents">
        <div className="upload-documents-content">
          <div className="file-upload-section">
            <FormInputLabel
              field="documentsUploadedToSharePoint"
              displayName="Supporting documents (PDF)"
              classes="forced-required-label"
              disabled={disabled}
            />
            {!isUploading && !selectedFile && (
              <div className="file-input-container">
                <div className="file-input-row">
                  <div
                    className={`file-input-wrapper ${fileError ? 'file-input-wrapper--error' : ''
                      }`}
                  >
                    <input
                      ref={fileInputRef}
                      id="documentsUploadedToSharePoint"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      data-testid="file-input"
                      multiple={false}
                      disabled={isUploadDisabled}
                    />
                    <button
                      type="button"
                      className="choose-files-button"
                      onClick={handleChooseFiles}
                      data-testid="choose-files-button"
                      disabled={isUploadDisabled}
                    >
                      Choose File
                    </button>
                    <span className="no-file-chosen">No file chosen</span>
                  </div>
                  <div className="file-requirements">
                    <div>
                      PDF files only. {getMaxFileUploadSizeMB()} MB max size per
                      file.
                    </div>
                    <div>
                      {getMaxUploadFilenameLength()} characters maximum filename
                      length
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isUploading && selectedFile && !uploadState.isFailed && (
              <div className="file-selected-container">
                <div
                  className={`file-selected-item ${fileError ? 'file-selected-item--error' : ''
                    }`}
                >
                  <span className="file-name">{selectedFile.name}</span>
                  <button
                    type="button"
                    className="delete-file-button"
                    onClick={handleClearFile}
                    data-testid="delete-file-button"
                    aria-label="Delete file"
                  >
                    <img src={'/images/icon-trash.svg'} alt="Delete" />
                  </button>
                </div>
                {fileError && (
                  <div className="file-error" data-testid="file-error">
                    {fileError}
                  </div>
                )}
                <div className="file-actions">
                  <ActionButton
                    onClick={handleClearFile}
                    classes="button no-wrap-text cancel-button"
                    dataTestId="clear-file-button"
                    disabled={disabled || isUploading}
                  >
                    Clear File
                  </ActionButton>
                  <ActionButton
                    onClick={handleUploadFile}
                    classes="button--secondary submit-button"
                    dataTestId="upload-file-button"
                    disabled={isUploadDisabled || isUploading || !!fileError}
                  >
                    Upload File
                  </ActionButton>
                </div>
              </div>
            )}

            {!isUploading && selectedFile && uploadState.isFailed && (
              <div className="file-selected-container">
                <div className="file-selected-item file-selected-item--error">
                  <span className="file-name">{selectedFile.name}</span>
                </div>

                {uploadState.error && (
                  <div className="file-error" data-testid="file-error">
                    {uploadState.error}
                  </div>
                )}
                <div className="file-actions">
                  <ActionButton
                    onClick={handleTryAgain}
                    classes="button no-wrap-text cancel-button"
                    dataTestId="try-again-button"
                    disabled={disabled || isUploading}
                  >
                    Try again
                  </ActionButton>
                </div>
              </div>
            )}

            {isUploading && selectedFile && (
              <div className="file-upload-progress">
                <FileUploadStatus
                  fileType={selectedFile.name}
                  percent={uploadState.progress}
                  isCompleted={uploadState.isCompleted}
                  isFailed={uploadState.isFailed}
                  error={uploadState.error}
                />
                <div className="progress-ruler"></div>
              </div>
            )}

            {!isUploading && uploadState.isFailed && !selectedFile && (
              <div className="file-selected-container">
                <div className="file-selected-item file-selected-item--error">
                  <span className="file-name">Upload Failed</span>
                </div>

                {uploadState.error && (
                  <div className="file-error" data-testid="file-error">
                    {uploadState.error}
                  </div>
                )}
                <div className="file-actions">
                  <ActionButton
                    onClick={handleTryAgain}
                    classes="button no-wrap-text cancel-button"
                    dataTestId="try-again-button"
                    disabled={disabled || isUploading}
                  >
                    Try again
                  </ActionButton>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="uploaded-documents-section">
          <h3 className="table-title">List of Uploaded documents</h3>
          {deleteError && (
            <div className="delete-error" data-testid="delete-error">
              {deleteError}
            </div>
          )}
          <div className="uploaded-documents-table-container">
            <table
              className="uploaded-documents-table"
              data-testid="uploaded-documents-table"
            >
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Uploaded By</th>
                  <th>Uploaded Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {uploadedDocuments.length > 0 ? (
                  uploadedDocuments.map((doc) => {
                    const originalFile = filesToUse.find(
                      (file) => file.id === doc.id
                    );
                    return (
                      <tr key={doc.id} data-testid={`document-row-${doc.id}`}>
                        <td>
                          <button
                            type="button"
                            className="document-name-link"
                            onClick={() =>
                              originalFile &&
                              handleDownloadDocument(originalFile)
                            }
                            data-testid={`download-document-${doc.id}`}
                            aria-label={`Download ${doc.documentName}`}
                          >
                            {doc.documentName}
                          </button>
                        </td>
                        <td>{doc.uploadedBy}</td>
                        <td>{doc.uploadDate}</td>
                        <td>
                          {!isApproved && (
                            <button
                              type="button"
                              className="delete-document-button"
                              onClick={() => handleDeleteDocument(doc.id)}
                              disabled={
                                isDeleting === doc.id ||
                                isDocumentDeleteDisabled(doc.id)
                              }
                              data-testid={`delete-document-${doc.id}`}
                              aria-label={`Delete ${doc.documentName}`}
                            >
                              {isDeleting === doc.id ? (
                                <div className="delete-loading-spinner">
                                  <div></div>
                                  <div></div>
                                  <div></div>
                                  <div></div>
                                </div>
                              ) : (
                                <img
                                  src={'/images/icon-trash.svg'}
                                  alt="Delete"
                                />
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="no-documents"
                      data-testid="no-documents"
                    >
                      No documents uploaded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default ExitAdjustmentUploadDocuments;
